/**
 * Error-name extraction for app-health error events (RFC-0974 follow-up,
 * spec §4, §5.2/§5.3).
 *
 * Forwards the thrown value's `name` VERBATIM — no allowlist, no shape check,
 * no truncation. This deliberately replaces the original closed seven-value
 * set: with only six builtin names allowed through, every `new Error(...)`,
 * every `DOMException`, every failed dynamic import and every non-Error
 * rejection collapsed into a single `OtherError` bucket — which in practice was
 * ~every event, leaving the app-health dashboard unable to say what actually
 * broke. The raw name is the thing that makes the metric actionable, and no
 * allowlist can anticipate the names worth seeing.
 *
 * §4 stance. `name` is a class name, not content: we still never read the
 * message, the stack, the filename, or a rejection value. `name` is a writable
 * string, so a library could in principle put something else there; the
 * collector remains the backstop (it drops any string containing `@` and
 * truncates over-long values before egress).
 *
 * Two sentinels cover the cases where there is no name to read at all.
 */

/** Raw class name, or one of the sentinels below. */
export type ErrorName = string

/** The `error` / `reason` was null or undefined — no throwable was attached.
 *  Chrome reports `ResizeObserver loop completed …` and cross-origin "Script
 *  error." this way, so separating it keeps that noise out of the real bugs. */
export const ERROR_NAME_NONE = 'NoErrorObject'

/** Thrown/rejected with a non-Error value (string, number, object with no
 *  string `name`) — a distinct failure mode from a named throwable. */
export const ERROR_NAME_NON_ERROR = 'NonErrorRejection'

/**
 * A thrown value / rejection reason. Typed as a small union rather than
 * `unknown`/`any` (both forbidden by the repo TS gate) — the DOM types
 * (`ErrorEvent.error`, `PromiseRejectionEvent.reason`) are `any` and assign to
 * this union at the call sites with no cast.
 */
type ThrownValue = Error | { name?: string | null } | string | number | boolean | null | undefined

/**
 * Read the raw class name off an arbitrary thrown value / rejection reason.
 * Reads ONLY `err.name` — never the message/stack.
 *
 *   - nullish                      ⇒ `NoErrorObject`
 *   - object with a string `name`  ⇒ that name, verbatim
 *   - anything else                ⇒ `NonErrorRejection`
 */
export function readErrorName(err: ThrownValue): ErrorName {
  try {
    if (err === null || err === undefined) return ERROR_NAME_NONE
    if (typeof err === 'object' && 'name' in err) {
      const name = err.name
      if (typeof name === 'string') return name
    }
    return ERROR_NAME_NON_ERROR
  } catch {
    /* never let extraction throw */
    return ERROR_NAME_NON_ERROR
  }
}
